import { BonusOperation } from '@@types'
import { gql, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import { useAuth } from '@features/auth'

type BonusHistoryData = {
  bonusHistory: BonusOperation[]
}

type BonusHistoryVariables = {
  from?: number
  to?: number
  limit?: number
  offset?: number
}

const DEFAULT_LIMIT = 10

export const GQL_QUERY_BONUS_HISTORY = gql`
  query bonusHistory (
    $from: Timestamp
    $to: Timestamp
    $limit: Int
    $offset: Int
  ) {
    bonusHistory(
      from: $from
      to: $to
      limit: $limit
      offset: $offset
    ) {
      id
      accountType
      currencyName
      type
      amount
      postFix
      saledAt
      operationAt
      activationAt
      expireAt
      purchaseId
      comment
    }
  }
`

export const useMyBonusHistory = (variables?: BonusHistoryVariables) => {
  const { isAuthorized, requireRegistration } = useAuth()
  const [ loadingMore, setLoadingMore ] = useState(false)
  const [ hasMore, setHasMore ] = useState(true)
  const [ offset, setOffset ] = useState(0)
  
  const limit = variables?.limit ?? DEFAULT_LIMIT
  
  const { data, loading, error, refetch, fetchMore } = useQuery<BonusHistoryData, BonusHistoryVariables>(
    GQL_QUERY_BONUS_HISTORY,
    {
      variables: {
        limit,
        offset: 0,
        ...variables
      },
      fetchPolicy: 'cache-and-network',
      skip: !isAuthorized || requireRegistration,
      onCompleted: (data) => {
        setOffset(data.bonusHistory.length)
      }
    }
  )

  const operations = useMemo(() => data?.bonusHistory ?? [], [ data?.bonusHistory ])

  const fetchNextPage = async () => {
    if (loading || loadingMore || !hasMore) return

    setLoadingMore(true)

    await fetchMore({
      variables: {
        limit,
        offset
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const prev = previousResult.bonusHistory ?? []
        const curr = fetchMoreResult.bonusHistory ?? []

        setOffset(prev.length + curr.length)

        if (curr.length < limit) {
          setHasMore(false)
        }

        if (curr.length === 0) {
          return previousResult
        }

        return {
          ...previousResult,
          bonusHistory: [ ...prev, ...curr ]
        }
      }
    })

    setLoadingMore(false)
  }

  return {
    operations,
    data,
    loading,
    loadingMore,
    error,
    hasMore,
    refetch,
    fetchMore,
    fetchNextPage
  }
}
